import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getUserMailbox } from "../Inbox";
import {
  Settings as SettingsIcon, Save, Phone, Clock, MessageSquare,
  ShieldCheck, AlertTriangle, CheckCircle2, Bot, Smartphone, Mail, Send, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const DEFAULT_SETTINGS = {
  primaryNumber: "+1 (760) 840-2918",
  carrierTrunk: "Twibri HD Direct Trunk A (US 10DLC Verified)",
  forwardCallNumber: "+1 (555) 234-5678",
  inboundFallback: "ai-copilot",
  businessHoursStart: "09:00",
  businessHoursEnd: "18:00",
  businessDays: ["mon", "tue", "wed", "thu", "fri"],
  autoResponderEnabled: true,
  autoResponderText: "Hi there! Thank you for contacting Colibri. We have received your message outside our standard office hours (9 AM - 6 PM EST). Our team or AI Copilot will respond first thing in the morning!",
  dndKeywords: "STOP, UNSUBSCRIBE, CANCEL, END, QUIT",
  dndAutoTag: true
};

export default function InboxSettingsTab() {
  const { user } = useAuth();
  const userMailbox = getUserMailbox(user);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem("ld_inbox_settings");
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return DEFAULT_SETTINGS;
  });

  const [isSaving, setIsSaving] = useState(false);
  const [testingPingEmail, setTestingPingEmail] = useState(null);

  const handleTestEmailPing = async (email, name) => {
    setTestingPingEmail(email);
    try {
      const { data } = await api.post("/inbox/send-email", {
        to_email: email,
        to_name: name,
        subject: `[Verified] ${name} Mailbox Handshake Ping`,
        body: `Hello ${name},\n\nThis confirms your mailbox (${email}) is connected, authenticated, and verified for real outbound and inbound communication on LaunchDeck CRM.\n\nTime: ${new Date().toLocaleString()}`,
        from_email: email,
        from_name: name
      });
      toast.success(`SMTP Handshake 200 OK! Test ping delivered to ${email} via ${data.provider?.toUpperCase() || 'Brevo'}`);
    } catch (err) {
      toast.error(`Handshake error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setTestingPingEmail(null);
    }
  };


  const handleToggleDay = (day) => {
    setSettings(prev => {
      const exists = prev.businessDays.includes(day);
      return {
        ...prev,
        businessDays: exists
          ? prev.businessDays.filter(d => d !== day)
          : [...prev.businessDays, day]
      };
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsSaving(true);
    setTimeout(() => {
      localStorage.setItem("ld_inbox_settings", JSON.stringify(settings));
      setIsSaving(false);
      toast.success("Messaging and channel settings saved successfully!");
    }, 400);
  };

  return (
    <div className="flex-1 h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <SettingsIcon className="w-6 h-6 text-blue-600" />
              Messaging & Channel Settings
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Configure outbound phone lines, carrier compliance rules, business hours, and automated auto-replies.
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-xs"
          >
            <Save className="w-4 h-4" />
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

                <form onSubmit={handleSave} className="space-y-6">
          {/* Section 0: Connected Multi-Account Mailboxes */}
          <Card className="p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <h2 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                    Connected Multi-Account Mailboxes & Sending Identities
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    All 3 corporate email accounts are merged into the unified CRM inbox with individual outbound sender routing.
                  </p>
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs">
                1 Verified Mailbox
              </Badge>
            </div>

            <div className="max-w-md">
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-full font-bold text-xs flex items-center justify-center ${userMailbox.avatarBg}`}>
                    {(userMailbox.name || "MB").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-800">
                    <Check className="w-3 h-3 text-emerald-500" /> Synced & Active
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{userMailbox.name}</h3>
                  <p className="text-xs font-mono text-slate-500">{userMailbox.email}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{userMailbox.service}</p>
                </div>
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Primary Outbound Mailbox
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleTestEmailPing(userMailbox.email, userMailbox.name)}
                    disabled={testingPingEmail === userMailbox.email}
                    className="h-7 px-2.5 text-xs text-blue-600 border-blue-200 dark:border-blue-800"
                  >
                    {testingPingEmail === userMailbox.email ? "Testing..." : "Test Ping"}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
          {/* Section 1: Carrier & Numbers */}
          <Card className="p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-blue-600" />
                <h2 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                  Carrier Lines & Number Routing
                </h2>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs">
                A2P 10DLC Verified
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Primary Outbound SMS & Voice Number
                </label>
                <Input
                  value={settings.primaryNumber}
                  onChange={(e) => setSettings({ ...settings, primaryNumber: e.target.value })}
                  className="text-xs mt-1 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">Trunk: {settings.carrierTrunk}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Inbound Voice Forwarding Number
                </label>
                <Input
                  value={settings.forwardCallNumber}
                  onChange={(e) => setSettings({ ...settings, forwardCallNumber: e.target.value })}
                  className="text-xs mt-1 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">Direct forward when human operator available</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Inbound Unanswered Fallback Action
              </label>
              <select
                value={settings.inboundFallback}
                onChange={(e) => setSettings({ ...settings, inboundFallback: e.target.value })}
                className="w-full mt-1 h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs dark:bg-slate-950 dark:border-slate-800"
              >
                <option value="ai-copilot">AI Sales Copilot answers instantly (Voice HD)</option>
                <option value="voicemail">Send to Voicemail with Instant AI Transcription</option>
                <option value="sms-nudge">Immediate Auto-SMS Missed-Call Text Back</option>
              </select>
            </div>
          </Card>

          {/* Section 2: Business Hours & After-Hours Auto-Responder */}
          <Card className="p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                  Office Hours & Auto-Responder
                </h2>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Operating Days
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { key: "mon", label: "Mon" },
                  { key: "tue", label: "Tue" },
                  { key: "wed", label: "Wed" },
                  { key: "thu", label: "Thu" },
                  { key: "fri", label: "Fri" },
                  { key: "sat", label: "Sat" },
                  { key: "sun", label: "Sun" }
                ].map(({ key, label }) => {
                  const active = settings.businessDays.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleToggleDay(key)}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                        active
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Daily Open Time</label>
                <Input
                  type="time"
                  value={settings.businessHoursStart}
                  onChange={(e) => setSettings({ ...settings, businessHoursStart: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Daily Close Time</label>
                <Input
                  type="time"
                  value={settings.businessHoursEnd}
                  onChange={(e) => setSettings({ ...settings, businessHoursEnd: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-xs text-slate-900 dark:text-slate-100">
                    Enable After-Hours Auto-Responder
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Automatically text back leads who message outside your operating schedule
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoResponderEnabled}
                  onChange={(e) => setSettings({ ...settings, autoResponderEnabled: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              {settings.autoResponderEnabled && (
                <Textarea
                  value={settings.autoResponderText}
                  onChange={(e) => setSettings({ ...settings, autoResponderText: e.target.value })}
                  className="text-xs min-h-[80px] mt-2"
                  placeholder="Type auto-reply message..."
                />
              )}
            </div>
          </Card>

          {/* Section 3: Carrier Compliance & DND */}
          <Card className="p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                Carrier Compliance & Opt-Out Keywords
              </h2>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Stop / Opt-Out Trigger Keywords (Comma separated)
              </label>
              <Input
                value={settings.dndKeywords}
                onChange={(e) => setSettings({ ...settings, dndKeywords: e.target.value })}
                className="text-xs mt-1 font-mono uppercase"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                US CTIA regulatory mandate. Incoming messages matching these words immediately cease automated outbound sends.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="dndAutoTag"
                checked={settings.dndAutoTag}
                onChange={(e) => setSettings({ ...settings, dndAutoTag: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="dndAutoTag" className="text-xs text-slate-700 dark:text-slate-300">
                Automatically mark contact as "Do Not Disturb (DND)" and trigger compliance notification
              </label>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-xs"
            >
              <Save className="w-4 h-4" />
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

